import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

const fmt = (n) => n.toLocaleString("pt-BR");

/** Números visíveis: 1 … 4 5 [6] 7 8 … 45 */
function pageList(page, pages) {
  const set = new Set([1, pages, page - 2, page - 1, page, page + 1, page + 2]);
  const nums = [...set].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i && n - nums[i - 1] > 1) out.push("…" + n);
    out.push(n);
  });
  return out;
}

/**
 * Paginação da lista. `compact` = só resumo + anterior/próxima (topo da lista).
 * onChange recebe a nova página.
 */
export const Pager = ({ page, perPage, total, onChange, compact = false, testid }) => {
  const pages = Math.max(1, Math.ceil(total / perPage));
  const [goto, setGoto] = useState("");
  if (total === 0) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  const go = (p) => onChange(Math.min(Math.max(1, p), pages));

  const summary = (
    <span className="text-sm text-slate-500">
      Mostrando <strong className="text-slate-700">{fmt(from)}–{fmt(to)}</strong> de <strong className="text-slate-700">{fmt(total)}</strong>
      {pages > 1 && <> · página {fmt(page)} de {fmt(pages)}</>}
    </span>
  );

  if (compact) {
    return (
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {summary}
        {pages > 1 && (
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => go(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => go(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-4 border-t border-slate-100" data-testid={testid}>
      {summary}
      {pages > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap lg:ml-auto">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => go(1)} title="Primeira página"><ChevronsLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => go(page - 1)}><ChevronLeft className="h-4 w-4 mr-1" /> Anterior</Button>
          <div className="hidden sm:flex items-center gap-1">
            {pageList(page, pages).map((n) =>
              typeof n === "string" ? (
                <span key={n} className="px-1 text-slate-400">…</span>
              ) : (
                <button key={n} onClick={() => go(n)}
                  className={`h-8 min-w-[2rem] px-2 rounded-md text-sm font-medium ${n === page ? "bg-[#232D4F] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  {n}
                </button>
              ))}
          </div>
          <Button size="sm" disabled={page >= pages} onClick={() => go(page + 1)} className="text-white font-semibold" style={{ background: "#1E9E2A" }} data-testid={testid ? `${testid}-next` : undefined}>
            Próxima página <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => go(pages)} title="Última página"><ChevronsRight className="h-4 w-4" /></Button>
          <form className="flex items-center gap-1 ml-1" onSubmit={(e) => { e.preventDefault(); if (goto) go(parseInt(goto, 10) || 1); setGoto(""); }}>
            <span className="text-xs text-slate-400">Ir para</span>
            <input value={goto} onChange={(e) => setGoto(e.target.value.replace(/\D/g, ""))} inputMode="numeric"
              className="h-8 w-14 rounded-md border border-slate-200 bg-white px-2 text-sm" placeholder={String(page)} />
          </form>
        </div>
      )}
    </div>
  );
};
